// Cargador idempotente de checkout.js de North.
//
// Existe para quitar un spinner del checkout: hoy PaymentStep encadena tres
// cosas al montarse (POST de sesión → descarga del script → mount del iframe) y
// el cliente ve dos esperas seguidas. Precalentar el script desde el carrito
// deja `window.checkout` listo antes de que llegue a pagar.
//
// Solo se precalienta el SCRIPT. La sesión NO: lleva el monto, y precrearla en
// el carrito cobraría el carrito viejo si el cliente cambia cantidades después.
//
// La inyección al DOM se inyecta por parámetro para poder testear la
// idempotencia en el entorno "node" de Jest, que no tiene document.

declare global {
  interface Window {
    checkout?: {
      mount(token: string, containerId: string): Promise<unknown>
      onPaymentComplete?(callback: (data: unknown) => void): () => void
      submit?(): Promise<unknown>
    }
  }
}

export const NORTH_CHECKOUT_SCRIPT_URL = "https://checkout.north.com/checkout.js"

type InjectFn = () => Promise<void>

/**
 * Memoiza la promesa: N llamadas concurrentes o secuenciales producen UNA sola
 * inyección. Un fallo limpia la memo para que se pueda reintentar.
 */
export function createCheckoutScriptLoader(inject: InjectFn): () => Promise<void> {
  let pending: Promise<void> | null = null
  return function load(): Promise<void> {
    if (pending) return pending
    pending = inject().catch((err) => {
      pending = null
      throw err
    })
    return pending
  }
}

/** Inyección real al DOM. Solo se usa en el browser. */
function injectScriptTag(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof document === "undefined") {
      reject(new Error("checkout.js requires a browser environment"))
      return
    }
    // Ya lo cargó otra instancia (o un render anterior): no re-descargar.
    if (window.checkout) {
      resolve()
      return
    }
    // Ya hay una etiqueta con ese src (montaje anterior, HMR, o un fallo que
    // limpió la memoización). NO registres un listener de 'load': si el script
    // ya disparó ese evento, el listener nunca corre, la promesa nunca resuelve
    // y — al estar memoizada — queda colgada PARA SIEMPRE, dejando al cliente en
    // "Cargando formulario…" sin salida. `waitForCheckoutGlobal` no rescata nada
    // porque va secuenciado DESPUÉS de este await.
    // Resolver de inmediato es correcto: el sondeo de waitForCheckoutGlobal es
    // quien espera a que `window.checkout` aparezca, y ése sí tiene tope.
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${NORTH_CHECKOUT_SCRIPT_URL}"]`,
    )
    if (existing) {
      resolve()
      return
    }
    const el = document.createElement("script")
    el.src = NORTH_CHECKOUT_SCRIPT_URL
    el.async = true
    el.onload = () => resolve()
    el.onerror = () => reject(new Error("checkout.js failed to load"))
    document.body.appendChild(el)
  })
}

/** Cargador compartido del proceso. Llamarlo N veces descarga UNA vez. */
export const loadCheckoutScript = createCheckoutScriptLoader(injectScriptTag)

/**
 * `window.checkout` aparece un instante después del onload del script. Espera
 * activa acotada — el mismo bucle que PaymentStep ya hacía inline.
 */
export async function waitForCheckoutGlobal(maxAttempts = 10, delayMs = 50): Promise<void> {
  for (let i = 0; i < maxAttempts; i++) {
    if (typeof window !== "undefined" && window.checkout) return
    await new Promise((r) => setTimeout(r, delayMs))
  }
  throw new Error("checkout.js did not initialize")
}

/**
 * Espera a que el contenedor de montaje exista en el DOM antes de llamar a
 * `checkout.mount`. North hace `document.getElementById(id)` y lanza
 * "Element with ID … not found" si el div todavía no se renderizó.
 *
 * Por qué hace falta: PaymentStep llama a `mount` en el mismo efecto, justo
 * después de `setSessionReady(true)`, y ese div lo pinta React *como consecuencia*
 * de ese cambio de estado (va detrás de `showForm`). Cuando el script viene
 * precalentado desde el carrito, `loadCheckoutScript` + `waitForCheckoutGlobal`
 * resuelven en microtasks —antes de que React confirme ese render—, así que
 * `mount` corría contra un DOM sin el contenedor y el cliente veía
 * "No pudimos inicializar el formulario de pago". (Antes del precalentado, la
 * descarga del script daba ese respiro gratis y la carrera nunca se manifestaba.)
 * Espera activa acotada al commit del contenedor, en el mismo estilo que
 * `waitForCheckoutGlobal`.
 */
export async function waitForElement(id: string, maxAttempts = 40, delayMs = 25): Promise<void> {
  for (let i = 0; i < maxAttempts; i++) {
    if (typeof document !== "undefined" && document.getElementById(id)) return
    await new Promise((r) => setTimeout(r, delayMs))
  }
  throw new Error(`mount container #${id} not found`)
}
